import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
});

// Función para obtener el token CSRF
const getCSRFToken = () => {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  
  // Si no se encuentra en las cookies, buscar si hay un token en el localStorage como fallback
  if (!cookieValue) {
    cookieValue = localStorage.getItem('csrfToken');
  }
  
  return cookieValue;
};

// Función para obtener un nuevo token CSRF desde el servidor
const fetchCSRFToken = async () => {
  try {
    // Hacer una solicitud al endpoint que proporciona el token CSRF
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const response = await axios.get(`${baseUrl}/get-csrf-token/`, {
      withCredentials: true
    });
    
    // Si la respuesta tiene un token, guardarlo en localStorage como respaldo
    if (response.data && response.data.csrfToken) {
      localStorage.setItem('csrfToken', response.data.csrfToken);
      return response.data.csrfToken;
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

// Interceptor para añadir token de autenticación y CSRF
api.interceptors.request.use(
  async (config) => {
    // Añadir token de autenticación si existe (desde localStorage)
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Añadir token CSRF para métodos no seguros
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(config.method.toUpperCase())) {
      let csrfToken = getCSRFToken();
      
      // Si no hay token CSRF, intentar obtenerlo del servidor
      if (!csrfToken) {
        csrfToken = await fetchCSRFToken();
      }
      
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      } else {
        // Silenciar - no hay token CSRF disponible
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Función para renovar el token JWT
const refreshToken = async () => {
  try {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      throw new Error('No hay refresh token disponible');
    }
    
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    const response = await axios.post(`${baseUrl}/usuarios/auth/refresh/`, {
      refresh: storedRefreshToken
    }, {
      withCredentials: true
    });
    
    const { access, refresh } = response.data;
    localStorage.setItem('authToken', access);
    if (refresh) {
      localStorage.setItem('refreshToken', refresh);
    }
    
    return access;
  } catch (error) {
    // Limpiar tokens y redirigir al login
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('csrfToken');
    window.location.href = '/app/login';
    throw error;
  }
};

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';
    
    // Lista de endpoints que fallan silenciosamente (sin logs ni modals)
    const silentFailEndpoints = [
      '/insumos/stock_critico',
      '/insumos/'
    ];
    
    const isSilentFailEndpoint = silentFailEndpoints.some(endpoint => 
      requestUrl.includes(endpoint)
    );
    
    // Si es un endpoint que puede fallar silenciosamente, no loguear nada
    if (isSilentFailEndpoint && error.response?.status === 403) {
      return Promise.reject(error);
    }
    
    // Log de errores solo para endpoints que no son silent
    console.error('Error en la petición:', error.message);
    
    if (error.response) {
      const status = error.response.status;
      const isAuthEndpoint = originalRequest.url?.includes('/auth/login') || 
                             originalRequest.url?.includes('/auth/refresh');
      
      if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          return Promise.reject(error);
        }
      }
      
      if (status === 403) {
        const detail = error.response.data?.detail || '';
        
        if (detail.includes('periodo de prueba ha finalizado') || 
            detail.includes('límite de prueba gratuita') || 
            detail.includes('plan actual no incluye')) {
          window.dispatchEvent(new CustomEvent('show-upgrade-modal', { 
            detail: { type: 'limit_reached', message: detail } 
          }));
          return Promise.reject(error);
        }

        if (!originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const newCSRFToken = await fetchCSRFToken();
            if (newCSRFToken) {
              originalRequest.headers['X-CSRFToken'] = newCSRFToken;
              return api(originalRequest);
            }
          } catch (retryError) {
            // Silenciar - el retry falló y se manejará como error normal
          }
        }
      }
    }
    
    return Promise.reject(error);
  }
);

export default api;